import { Injectable } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { dayjs } from '@karve.it/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { ChartOptions } from 'chart.js';
import { EMPTY, Observable, combineLatest, filter, forkJoin, map, switchMap } from 'rxjs';

import { DataOnlyGQL, PreviewReportQueryVariables } from '../../../generated/graphql.generated';
import { GraphQLModule } from '../../core/public-api';
import { DemoModeService } from '../../services/demo-mode.service';
import { currentTimeSeconds } from '../../time';
import { getDashboardErrorMessage } from '../../utilities/errors.util';

import { DashboardStore, mergeInDefaultFilters } from '../dashboardv2.store';

import {
  ANCHORS,
  DashboardTransactionsStore,
  TransactionAnchorOption,
  TransactionStatusOption,
  getTransactionsFilter,
} from './dashboard-transactions.store';

const chartOptions: ChartOptions<'bar'> = {
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        boxWidth: 12,
      },
    },
  },
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      display: true,
      stacked: true,
      grid: {
        color: 'transparent',
      },
      ticks: {
        color: '#BFC2C6',
      },
    },
    y: {
      display: true,
      stacked: true,
      grid: {
        color: 'rgba(191, 194, 198, .45)',
      },
      border: {
        dash: [ 5, 10 ],
      },
      ticks: {
        color: '#BFC2C6',
        maxTicksLimit: 5,
      },
      beginAtZero: true,
    },
  },
};

const PROVIDER_ORDER = [ 'BlueSage', 'Square', 'No Provider' ];

const PROVIDER_COLORS: Record<string, string> = {
  BlueSage: '--primary-color',
  Square: '--purple-500',
  'No Provider': '--gray-400',
};

const FALLBACK_PROVIDER_COLOR = '--teal-500';

interface EventsQueryVars {
  eventsCreated: PreviewReportQueryVariables;
  eventsHappening: PreviewReportQueryVariables;
}

interface TransactionsActivityState {
  transactionsPerDay: string[][];
  eventsCreatedPerDay: string[][];
  eventsHappeningPerDay: string[][];
  transactionsLoading: boolean;
  eventsLoading: boolean;
  transactionsError: string | null;
  eventsError: string | null;
}

const initialState: TransactionsActivityState = {
  transactionsPerDay: [],
  eventsCreatedPerDay: [],
  eventsHappeningPerDay: [],
  transactionsLoading: false,
  eventsLoading: false,
  transactionsError: null,
  eventsError: null,
};

@Injectable()
export class DashboardTransactionsActivityStore extends ComponentStore<TransactionsActivityState> {

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly transactionsStore: DashboardTransactionsStore,
    private readonly dataOnlyGQL: DataOnlyGQL,
    private readonly graphqlModule: GraphQLModule,
    private readonly demoModeService: DemoModeService,
  ) {
    super(initialState);
  }

  private readonly transactionsQueryVars$ = this.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.transactionsStore.status$,
    this.transactionsStore.anchor$,
    this.graphqlModule.zone,
    (currency, { start, end }, status, anchor, zone) => {
      if (!zone) return null;
      return this.getTransactionsQueryVars({ start, end, currency, status, anchor });
    },
    { debounce: true },
  ).pipe(filter(Boolean));

  private readonly eventsQueryVars$ = this.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.graphqlModule.zone,
    (currency, { start, end }, zone) => {
      if (!zone) return null;
      return this.getEventsQueryVars({ start, end, currency });
    },
    { debounce: true },
  ).pipe(filter(Boolean));

  readonly reloadTransactions = this.effect(() => this.whenActive(this.transactionsQueryVars$).pipe(
    switchMap(([ queryVars, isDemo ]) => {
      if (isDemo) {
        this.patchState({ transactionsPerDay: [], transactionsLoading: false, transactionsError: null });
        return EMPTY;
      }
      this.patchState({ transactionsLoading: true, transactionsError: null });
      return this.dataOnlyGQL.fetch(queryVars, { fetchPolicy: 'network-only' }).pipe(tapResponse({
        next: (res) => {
          this.patchState({
            transactionsPerDay: res.data.previewReport.data.rows,
            transactionsLoading: false,
          });
          this.dashboardStore.setLastReload(currentTimeSeconds());
        },
        error: (err: ApolloError) => {
          this.patchState({ transactionsLoading: false, transactionsError: getDashboardErrorMessage(err) });
        },
      }));
    }),
  ));

  readonly reloadEvents = this.effect(() => this.whenActive(this.eventsQueryVars$).pipe(
    switchMap(([ queryVars, isDemo ]) => {
      if (isDemo) {
        this.patchState({ eventsCreatedPerDay: [], eventsHappeningPerDay: [], eventsLoading: false, eventsError: null });
        return EMPTY;
      }
      this.patchState({ eventsLoading: true, eventsError: null });
      return forkJoin({
        eventsCreated: this.dataOnlyGQL.fetch(queryVars.eventsCreated, { fetchPolicy: 'network-only' }),
        eventsHappening: this.dataOnlyGQL.fetch(queryVars.eventsHappening, { fetchPolicy: 'network-only' }),
      }).pipe(tapResponse({
        next: (res) => {
          this.patchState({
            eventsCreatedPerDay: res.eventsCreated.data.previewReport.data.rows,
            eventsHappeningPerDay: res.eventsHappening.data.previewReport.data.rows,
            eventsLoading: false,
          });
          this.dashboardStore.setLastReload(currentTimeSeconds());
        },
        error: (err: ApolloError) => {
          this.patchState({ eventsLoading: false, eventsError: getDashboardErrorMessage(err) });
        },
      }));
    }),
  ));

  private readonly queryParams$ = this.select(
    this.transactionsQueryVars$,
    this.eventsQueryVars$,
    this.dashboardStore.timezone$,
    (transactionsQueryVars, eventsQueryVars, timezone) => ({
      transactions: this.dashboardStore.queryVarsToQueryParams(transactionsQueryVars, timezone),
      eventsCreated: this.dashboardStore.queryVarsToQueryParams(eventsQueryVars.eventsCreated, timezone),
      eventsHappening: this.dashboardStore.queryVarsToQueryParams(eventsQueryVars.eventsHappening, timezone),
    }),
  );

  readonly viewModel$ = this.select(
    this.state$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.isMonthMode$,
    this.dashboardStore.displayPeriod$,
    this.queryParams$,
    this.dashboardStore.timezone$,
    (state, period, isMonthMode, displayPeriod, queryParams, timezone) => ({
      loading: state.transactionsLoading || state.eventsLoading,
      error: state.transactionsError || state.eventsError,
      displayPeriod,
      queryParams,
      options: chartOptions,
      graphData: this.toGraphData(state, period, isMonthMode, timezone),
    }),
  );

  private whenActive<T>(queryVars$: Observable<T>) {
    return combineLatest([
      combineLatest([
        queryVars$,
        this.dashboardStore.routeActivated$,
        this.dashboardStore.reloadKey$,
      ]).pipe(
        filter(([ , path ]) => path === 'payments'),
        map(([ queryVars ]) => queryVars),
      ),
      this.demoModeService.isDemo$,
    ]);
  }

  private toGraphData(
    state: TransactionsActivityState,
    period: { start: number; end: number },
    isMonthMode: boolean,
    timezone: string,
  ) {
    const dates: string[] = [];
    const labels: string[] = [];

    const lastDate = dayjs.tz(period.end * 1000, timezone).format('YYYY-MM-DD');

    let day = dayjs(dayjs.tz(period.start * 1000, timezone).format('YYYY-MM-DD'));

    while (day.format('YYYY-MM-DD') <= lastDate) {
      dates.push(day.format('YYYY-MM-DD'));
      labels.push(isMonthMode ? day.format('DD') : day.format('YYYY-MM-DD'));
      day = day.add(1, 'day');
    }

    const documentStyle = getComputedStyle(document.documentElement);

    const providerDatasets = this.toProviderMaps(state.transactionsPerDay).map(({ provider, amounts }) => ({
      type: 'bar' as const,
      label: provider,
      data: this.toCumulative(dates, amounts),
      backgroundColor: documentStyle.getPropertyValue(PROVIDER_COLORS[provider] || FALLBACK_PROVIDER_COLOR),
      stack: 'transactions',
      order: 1,
    }));

    return {
      labels,
      datasets: [
        ...providerDatasets,
        {
          type: 'line' as const,
          label: 'Created events',
          data: this.toCumulative(dates, this.toMap(state.eventsCreatedPerDay)),
          borderColor: documentStyle.getPropertyValue('--orange-500'),
          backgroundColor: documentStyle.getPropertyValue('--orange-500'),
          borderWidth: 3,
          stack: 'createdEvents',
          order: 0,
        },
        {
          type: 'line' as const,
          label: 'Scheduled events',
          data: this.toCumulative(dates, this.toMap(state.eventsHappeningPerDay)),
          borderColor: documentStyle.getPropertyValue('--info'),
          backgroundColor: documentStyle.getPropertyValue('--info'),
          borderWidth: 3,
          stack: 'scheduledEvents',
          order: 0,
        },
      ],
    };
  }

  private toProviderMaps(rows: string[][]) {
    const byProvider: Record<string, Record<string, number>> = {};

    for (const [ date, provider, amount ] of rows) {
      byProvider[provider] = byProvider[provider] || {};
      byProvider[provider][date] = (byProvider[provider][date] || 0) + parseFloat(amount);
    }

    const rank = (provider: string) => {
      const index = PROVIDER_ORDER.indexOf(provider);
      return index === -1 ? PROVIDER_ORDER.length : index;
    };

    return Object.keys(byProvider)
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
      .map((provider) => ({ provider, amounts: byProvider[provider] }));
  }

  private toCumulative(dates: string[], amounts: Record<string, number>) {
    let cumulative = 0;

    return dates.map((date) => {
      cumulative += amounts[date] || 0;
      return cumulative;
    });
  }

  private toMap(rows: string[][]) {
    const output: Record<string, number> = {};

    for (const [ date, amount ] of rows) {
      output[date] = (output[date] || 0) + parseFloat(amount);
    }

    return output;
  }

  private getTransactionsQueryVars(input: {
    start: number;
    end: number;
    currency: string;
    status: TransactionStatusOption;
    anchor: TransactionAnchorOption;
  }): PreviewReportQueryVariables {
    const { periodType, dateColumn } = ANCHORS[input.anchor];

    return {
      skip: 0,
      limit: -1,
      start: input.start,
      end: input.end,
      periodType,
      groupBy: dateColumn,
      filter: getTransactionsFilter(input.currency, input.status),
      dataColumns: [ dateColumn, 'transactionProvider', 'transactionPaidTotal' ],
      aggregationColumns: [],
      sort: `${ dateColumn } ASC`,
      performance: 'skip-count',
      linkFormat: 'sheets',
    };
  }

  private getEventsQueryVars(input: {
    start: number;
    end: number;
    currency: string;
  }): EventsQueryVars {
    const { start, end, currency } = input;

    const common = {
      skip: 0,
      limit: -1,
      start,
      end,
      filter: mergeInDefaultFilters(currency, {
        event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
        charge: { excludeDamages: true },
      }),
      aggregationColumns: [],
      performance: 'skip-count',
      linkFormat: 'sheets',
      currencySource: currency === 'All' ? 'zone' : undefined,
    };

    return {
      eventsCreated: {
        ...common,
        periodType: 'eventCreatedAt',
        groupBy: 'eventCreatedAt',
        dataColumns: [ 'eventCreatedAt', 'discountedSubTotal' ],
      },
      eventsHappening: {
        ...common,
        periodType: 'eventStart',
        groupBy: 'eventBookingDate',
        dataColumns: [ 'eventBookingDate', 'discountedSubTotal' ],
        sort: 'eventBookingDate ASC',
      },
    };
  }
}
